import { Validated } from '../model/general';
import { parse } from 'tldts';

export const ILLEGAL_CHARACTERS_MESSAGE = 'Illegal characters are used';
const INVALID_PORT_MESSAGE = 'Invalid Port';
const INVALID_MAX_LENGTH = 'Length is Too Long';
const NUMBER_REGEX = /^\d+$/;
export const MAX_NAME_LENGTH = 40;
const NAME_REGEX = '^([a-zA-Z0-9-_. ]{0,38}[a-zA-Z0-9-_.])?$';
const RULE_NAME_REGEX = '^[a-zA-Z]$|^[a-zA-Z]([a-zA-Z0-9_ -]{0,48})[a-zA-Z0-9_ -]$';
export const DUPLICATE_SERVICE_NAME = 'Connection name already exists';

export const checkPort = (value: string): string => {
    if (!value) {
        return '';
    }

    const number = Number.parseInt(value);
    if (!NUMBER_REGEX.test(value) || 1 > number || number > 65535) {
        return INVALID_PORT_MESSAGE;
    }

    return '';
};

export const convertAndCheckPorts = (value: string): string => {
    if (!value) {
        return '';
    }

    if (!value.trim()) {
        return INVALID_PORT_MESSAGE;
    }

    const ports = value.split(/[,|-]/).map(port => port.trim());
    for (const port of ports) {
        const validationResult = checkPort(port);
        if (validationResult !== '') {
            return INVALID_PORT_MESSAGE;
        }
    }

    return '';
};

export const maxLength = (limit: number) => {
    return (value: string): string => {
        return value.length <= limit ? '' : INVALID_MAX_LENGTH;
    };
};

export const nameValidation = (value: string, maxLength?: number) => {
    if (!value) {
        return '';
    }
    let regexp = NAME_REGEX;
    if (maxLength) {
        regexp = regexp.replace('{0,38}', `{0,${maxLength - 2}}`);
    }
    return new RegExp(regexp).test(value) && validateString(value, false) === '' ? '' : 'Invalid Name';
};

export const ruleNameValidation = (value: string) => {
    if (!value) {
        return '';
    }
    return new RegExp(RULE_NAME_REGEX).test(value) ? '' : 'Invalid Name';
};

export const isValueDuplicate = (value: any, originalValue?: any, tableData?: any[], key?: string) => {

    if (!key || !tableData) return '';

    let listToCompare = tableData;

    if (originalValue) {
        listToCompare = tableData.filter((item: any) => item[key] !== originalValue);
    }

    return listToCompare.some((item: any) => item[key] === value) ? 'Duplicated' : '';
};

const arraysAreEqual = (arr1: any[], arr2: any[]) => {
    const sortedArr1 = arr1.slice().sort((a, b) => a.localeCompare(b));
    const sortedArr2 = arr2.slice().sort((a, b) => a.localeCompare(b));

    return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
};

const toArray = (value: any) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string') {
        return value.replace(/\s+/g, '').split(',');
    }

    return [value];
};

export const isDuplicate = (newRow: any, originalData: any, tableData: any[], keyFields: string[]) => {
    let listToCompare = tableData;
    if (originalData) {
        listToCompare = tableData.filter(_ => keyFields.some((key: string) => (originalData[key] || null) !== (_[key] || null)));
    }

    return listToCompare.some((row: any) =>
        keyFields.every((key: string) => {
            const rowValue = row[key] || null;
            const newValue = newRow[key] || null;

            if (Array.isArray(rowValue)) {
                return arraysAreEqual(toArray(rowValue), toArray(newValue));
            } else {
                return rowValue === newValue;
            }
        })
    );
};

export const isDomainValid = (value: string): string => {

    const invalidDomainMessage = 'Invalid Domain';

    if (!value) return '';

    const domainParts = value.split('.');

    // Check the number of parts in the domain
    if (domainParts.length < 2 || domainParts.length > 3) {
        return invalidDomainMessage;
    }

    // Check no part is empty
    if (domainParts.some(_ => !_)) {
        return invalidDomainMessage;
    }

    // Validate the domain name (first part)
    const domainName = domainParts[0];
    const tld = value.slice(domainName.length + 1, value.length);

    // Updated regex to disallow hyphens at the start or end
    if (!/^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/.test(domainName)) {
        return invalidDomainMessage;
    }

    // Parse the domain URL using tldts
    const parsed = parse(value);

    // Check if the domain is valid
    if (!parsed.isIcann || !parsed.domain || parsed.domainWithoutSuffix?.includes('.')) {
        return invalidDomainMessage;
    }

    // Validate the TLD
   // const tld = parsed.publicSuffix;
    if (tld !== parsed.publicSuffix) {
        return invalidDomainMessage;
    }

    return '';
};

export const validateValue = (item: Validated<any> | undefined,
    updateValidationErrors: (updater: (prevErrors: Map<string, string>) => Map<string, string>) => void,
    accessor: any,
    value: any,
    setIsEditing?: (isEditing: boolean) => void,
    originalData?: any,
    tableData?: any[]) => {

    if (!item?.validationRules) {
        return true;
    }
    const errors = item.validationRules
        .map((rule) => rule(value, originalData?.[item.accessor], tableData, item.accessor))
        .filter((rule) => !!rule);

    if (errors.length) {
        updateValidationErrors((prevErrors) =>
            new Map(prevErrors.set(accessor, errors.join(', ')))
        );
        return false;
    } else {
        updateValidationErrors((prevErrors) => {
            prevErrors.delete(accessor);
            return new Map(prevErrors);
        });
        return true;
    }
};

export const validateString = (
    value: string,
    allowNewline = true,
    allowedChars: string[] = []
) => {
    // Base allowed characters, excluding the hyphen for now
    let allowedCharacterSet = 'A-Za-z0-9_., ';

    // Include newline if allowNewline is true
    if (allowNewline) {
        allowedCharacterSet += String.raw`\n`;
    }

    // Include additional allowed characters from the list
    if (allowedChars.length > 0) {
        const escapedChars = allowedChars.map(char => {
            // Convert special regex characters except for the hyphen
            return char.replace(/[-\\^$*+?.()|[\]{}]/g, String.raw`\$&`);
        }).join('');
        allowedCharacterSet += escapedChars;
    }

    // Finally, add the hyphen at the end to avoid range issues
    allowedCharacterSet += '-';

    // Construct the regex pattern
    const pattern = `^[${allowedCharacterSet}]+$`;
    const descFormatRegex = new RegExp(pattern);

    // Validate the input value
    return !value || descFormatRegex.test(value) ? '' : ILLEGAL_CHARACTERS_MESSAGE;
};

export const validateConnectionName = (value: string) => {
    const pattern = String.raw`^[A-Za-z0-9_.,\-/ ]+$`;
    const descFormatRegex = new RegExp(pattern);
    return !value || descFormatRegex.test(value) ? '' : ILLEGAL_CHARACTERS_MESSAGE;
};